// Shared helper functions for Location Management (add/edit)

// Checks if a value is blank (undefined, null, or empty string)
export const isBlank = (v) =>
  v === undefined || v === null || (typeof v === "string" && v.trim() === "");

// Parses the locationType string into an array of fields (lowercased)
export const parseTypeFields = (type) =>
  String(type ?? "")
    .split("/")
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => p.toLowerCase());

// Checks if an image is empty (undefined, null, empty string, or empty File-like object)
export const isEmptyImage = (img) => {
  if (img === undefined || img === null) return true;
  const hasFilename = typeof img.filename === "string" && img.filename.trim() !== "";
  const hasDataURL = typeof img.dataURL === "string" && img.dataURL.trim() !== "";
  return !(hasFilename || hasDataURL);
};

// Validates a location object and returns a tuple: [isValid, errors]
export const validateLocation = (newLocation) => {
  const errors = {};

  // Validate dynamic fields derived from locationType
  const fields = parseTypeFields(newLocation?.locationType);
  if (fields.length === 0) {
    errors.locationType = "Location type is required.";
  }

  for (const key of fields) {
    const val = newLocation?.[key];
    if (isBlank(val)) {
      errors[key] = `${key} is required.`;
      continue;
    }

    const s = String(val).trim();

    // Format validations only apply when the field is included in the location type
    switch (key) {
      case "lane":
        if (!/^L\d+$/.test(s)) {
          errors.lane = "Please enter the lane as L followed by a number, for example: L1.";
        }
        break;
      case "rack":
        if (!/^R\d+$/.test(s)) {
          errors.rack = "Please enter the rack as R followed by a number, for example: R1.";
        }
        break;
      case "shelf": {
        const m = /^S(\d+)-S(\d+)$/.exec(s);
        if (!m) {
          errors.shelf = "Please enter the shelf range like S1-S3. Use S<start>-S<end>, where the end is greater than the start.";
        } else {
          const start = parseInt(m[1], 10);
          const end = parseInt(m[2], 10);
          if (isNaN(start) || isNaN(end) || end <= start) {
            errors.shelf = "Shelf range is invalid. The second number must be larger than the first (for example S1-S3).";
          }
        }
        break;
      }
      case "drawer": {
        const m = /^D(\d+)-D(\d+)$/.exec(s);
        if (!m) {
          errors.drawer = "Please enter the drawer range like D1-D3. Use D<start>-D<end>, where the end is greater than the start.";
        } else {
          const start = parseInt(m[1], 10);
          const end = parseInt(m[2], 10);
          if (isNaN(start) || isNaN(end) || end <= start) {
            errors.drawer = "Drawer range is invalid. The second number must be larger than the first (for example D1-D3).";
          }
        }
        break;
      }
      default:
        break;
    }
  }

  // Validate city (explicit field)
  if (isBlank(newLocation?.city)) {
    errors.city = "City is required.";
  }

  // Validate imagePath: if an existing imagePath (starts with "Locations/") is present, skip imageData validation
  const hasImagePath =
    typeof newLocation?.imagePath === "string" &&
    newLocation.imagePath.startsWith("Locations/");
  if (!hasImagePath) {
    if (isEmptyImage(newLocation?.imageData)) {
      errors.imagePath = "An image is required.";
    }
  }

  return [Object.keys(errors).length === 0, errors];
};

// Concatenates location informations into a single locationKey string
// 'order' defines the order of fields to use when present in the type (defaults to ["room","lane","rack"]).
export const concatenateLocationKey = (newLocation, order = ["room", "lane", "rack"]) => {
  const fields = parseTypeFields(newLocation?.locationType);
  const fieldSet = new Set(fields);

  const parts = order
    .filter((key) => fieldSet.has(key))
    .map((key) => {
      const v = newLocation?.[key];
      return v === undefined || v === null ? "" : String(v).trim();
    })
    .filter(Boolean);

  return parts.join("");
};

export const validateLocationKey = (newLocation) => {
  const errors = {};
  const fields = parseTypeFields(newLocation?.locationType);

  // If no type fields defined, nothing to validate here
  if (fields.length === 0) {
    return [true, errors];
  }

  const expectedKey = String(concatenateLocationKey(newLocation, fields) ?? "").trim();

  // Always create/overwrite the locationKey with the concatenated one
  newLocation.locationKey = expectedKey;

  // Run full validation but only surface errors for room, lane and rack
  const [, allErrors] = validateLocation(newLocation);
  const allowed = new Set(["room", "lane", "rack"]);
  if (allErrors && typeof allErrors === "object") {
    for (const k of Object.keys(allErrors)) {
      if (allowed.has(k)) {
        errors[k] = allErrors[k];
      }
    }
  }

  // If expected key cannot be built because required fields are missing -> error
  if (!expectedKey) {
    errors.imagePath = "Location fields for the location type are required to upload an image";
    return [false, errors];
  }

  return [Object.keys(errors).length === 0, errors];
}
